import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@mantine/core';
import { IoChatbubblesOutline, IoClose, IoImageOutline, IoSend } from 'react-icons/io5';
import { Breadcrumbs } from '@components';
import { Button, Textarea } from '@shared/ui';
import { ChatBubbleImages } from '@modules/client-order-chat/components/ChatBubbleImages';
import {
  sellerSupportChatApi,
  SELLER_SUPPORT_CHAT_MAX_IMAGES,
} from '@modules/seller-support-chat/api/seller-support-chat.api';
import {
  appendSellerSupportMessageToCache,
  mergeSellerSupportMessages,
  SELLER_CHAT_MESSAGES_QUERY_KEY,
  SELLER_CHAT_UNREAD_QUERY_KEY,
} from '@modules/seller-support-chat/chat-cache';
import { useSellerSupportChatSocket } from '@modules/seller-support-chat/hooks/useSellerSupportChatSocket';
import type { SellerSupportMessage, SellerSupportMessagesResponse } from '@modules/seller-support-chat/types';
import '@shared/styles/page.css';
import './SellerSupportChatPage.css';

function messageKey(message: SellerSupportMessage) {
  return `${message.id}:${message.createdAt}`;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

/** Локальный день пользователя (браузерный часовой пояс). */
function chatDayKey(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function chatTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function chatDayLabel(iso: string) {
  const key = chatDayKey(iso);
  const today = chatDayKey(new Date().toISOString());
  const yesterday = chatDayKey(new Date(Date.now() - 86_400_000).toISOString());
  if (key === today) return 'Сегодня';
  if (key === yesterday) return 'Вчера';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return key;
  const withYear = d.getFullYear() !== new Date().getFullYear();
  return withYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

type PendingImage = {
  id: string;
  file: File;
  previewUrl: string;
};

export default function SellerSupportChatPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const messagesQuery = useQuery({
    queryKey: SELLER_CHAT_MESSAGES_QUERY_KEY,
    queryFn: async () => {
      const data = await sellerSupportChatApi.getMessages({ limit: 100 });
      const prev = queryClient.getQueryData<SellerSupportMessagesResponse>(
        SELLER_CHAT_MESSAGES_QUERY_KEY,
      );
      return mergeSellerSupportMessages(prev, data);
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const markReadMutation = useMutation({
    mutationFn: () => sellerSupportChatApi.markRead(),
    onSuccess: () => {
      queryClient.setQueryData(SELLER_CHAT_UNREAD_QUERY_KEY, { count: 0 });
    },
  });

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const sendMutation = useMutation({
    mutationFn: ({ body, images }: { body: string; images: File[] }) =>
      sellerSupportChatApi.sendMessage(body, images),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: SELLER_CHAT_MESSAGES_QUERY_KEY });
    },
    onSuccess: (message) => {
      setDraft('');
      clearPendingImages();
      appendSellerSupportMessageToCache(queryClient, message);
    },
  });

  const appendMessage = useCallback(
    (message: SellerSupportMessage) => {
      appendSellerSupportMessageToCache(queryClient, message);
      if (message.senderKind === 'SUPER_ADMIN') {
        queryClient.setQueryData(SELLER_CHAT_UNREAD_QUERY_KEY, { count: 0 });
        markReadMutation.mutate();
      }
    },
    [markReadMutation, queryClient],
  );

  useSellerSupportChatSocket({
    onMessage: ({ message }) => appendMessage(message),
  });

  useEffect(() => {
    if (!messagesQuery.isSuccess) return;
    markReadMutation.mutate();
  }, [messagesQuery.isSuccess]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messagesQuery.data?.items.length, sendMutation.isSuccess, pendingImages.length]);

  useEffect(() => {
    return () => {
      pendingImages.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke on unmount only
  }, []);

  const messages = messagesQuery.data?.items ?? [];

  const handlePickImages = (files: FileList | null) => {
    if (!files?.length) return;
    const next: PendingImage[] = [];
    const room = SELLER_SUPPORT_CHAT_MAX_IMAGES - pendingImages.length;
    Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, Math.max(room, 0))
      .forEach((file) => {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
    if (next.length === 0) return;
    setPendingImages((prev) => [...prev, ...next].slice(0, SELLER_SUPPORT_CHAT_MAX_IMAGES));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSend = () => {
    const body = draft.trim();
    const images = pendingImages.map((item) => item.file);
    if ((!body && images.length === 0) || sendMutation.isPending) return;
    sendMutation.mutate({ body, images });
  };

  const canSend = Boolean(draft.trim() || pendingImages.length > 0);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.id - b.id),
    [messages],
  );

  return (
    <div className="page-container seller-support-chat-page">
      <Breadcrumbs items={[{ label: 'Главная', path: '/' }, { label: 'Чат с поддержкой' }]} />

      <div className="page-header">
        <div className="page-header-top">
          <h1>Чат с поддержкой</h1>
        </div>
        <p className="seller-support-chat-page__subtitle">
          Вопросы по кабинету и заказам — напишите администратору — он ответит в ближайшее время.
        </p>
      </div>

      <div className="content-wrapper seller-support-chat-page__panel">
        <div ref={scrollRef} className="seller-support-chat-page__messages">
          {messagesQuery.isLoading ? (
            <div className="seller-support-chat-page__state">
              <Loader size="sm" color="var(--accent)" />
            </div>
          ) : null}

          {!messagesQuery.isLoading && sortedMessages.length === 0 ? (
            <div className="seller-support-chat-page__empty">
              <div className="seller-support-chat-page__empty-icon" aria-hidden>
                <IoChatbubblesOutline />
              </div>
              <p className="seller-support-chat-page__empty-title">Пока нет сообщений</p>
              <p className="seller-support-chat-page__empty-text">
                Напишите администратору — он ответит в ближайшее время.
              </p>
            </div>
          ) : null}

          {sortedMessages.map((message, index) => {
            const mine = message.senderKind === 'SELLER';
            const prev = sortedMessages[index - 1];
            const showDay =
              !prev || chatDayKey(prev.createdAt) !== chatDayKey(message.createdAt);
            const stacked = Boolean(
              prev &&
              !showDay &&
              prev.senderKind === message.senderKind,
            );
            const showAuthor = !stacked;
            const images = message.imageUrls ?? [];

            return (
              <div key={messageKey(message)} className="seller-support-chat-page__item">
                {showDay ? (
                  <div className="seller-support-chat-page__day">
                    <span>{chatDayLabel(message.createdAt)}</span>
                  </div>
                ) : null}
                <div
                  className={[
                    'seller-support-chat-page__bubble',
                    mine
                      ? 'seller-support-chat-page__bubble--mine'
                      : 'seller-support-chat-page__bubble--theirs',
                    stacked ? 'seller-support-chat-page__bubble--stacked' : '',
                    images.length > 0 ? 'seller-support-chat-page__bubble--has-images' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {showAuthor ? (
                    <div className="seller-support-chat-page__bubble-author">
                      {mine ? 'Вы' : message.senderName || 'Администратор'}
                    </div>
                  ) : null}
                  {images.length > 0 ? (
                    <ChatBubbleImages
                      urls={images}
                      classPrefix="seller-support-chat-page"
                    />
                  ) : null}
                  <div className="seller-support-chat-page__bubble-body">
                    {message.body ? (
                      <div className="seller-support-chat-page__bubble-text">{message.body}</div>
                    ) : null}
                    <time
                      className="seller-support-chat-page__bubble-time"
                      dateTime={message.createdAt}
                    >
                      {chatTime(message.createdAt)}
                    </time>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="seller-support-chat-page__composer">
          {pendingImages.length > 0 ? (
            <div className="seller-support-chat-page__composer-previews">
              {pendingImages.map((item) => (
                <div key={item.id} className="seller-support-chat-page__composer-preview">
                  <img src={item.previewUrl} alt="" />
                  <button
                    type="button"
                    className="seller-support-chat-page__composer-preview-remove"
                    onClick={() => removePendingImage(item.id)}
                    aria-label="Убрать фото"
                  >
                    <IoClose />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="seller-support-chat-page__composer-row">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => handlePickImages(event.currentTarget.files)}
            />
            <Button
              type="button"
              variant="secondary"
              className="seller-support-chat-page__composer-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={
                sendMutation.isPending || pendingImages.length >= SELLER_SUPPORT_CHAT_MAX_IMAGES
              }
              aria-label="Прикрепить фото"
            >
              <IoImageOutline />
            </Button>
            <Textarea
              className="seller-support-chat-page__composer-input"
              placeholder="Напишите сообщение…"
              minRows={1}
              maxRows={5}
              autosize
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button
              className="seller-support-chat-page__composer-send"
              onClick={handleSend}
              loading={sendMutation.isPending}
              disabled={!canSend}
              aria-label="Отправить"
            >
              <IoSend />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
